import { Stack } from "@mui/material";

// Third Party
import { v4 as uuidv4 } from "uuid";

import { firstCapitalLetter } from "@Common/helpers/Modifiers/characters";

// Icons
import BBB from "@/assets/icons/socialMedia/betterbusinessbureau.svg";
import Cars from "@/assets/icons/socialMedia/cars.svg";
import CarFax from "@/assets/icons/socialMedia/carFax.svg";
import CarGurus from "@/assets/icons/socialMedia/carGurus.svg";
import DealerRater from "@/assets/icons/socialMedia/dealerraterrounded.svg";
import Edmunds from "@/assets/icons/socialMedia/edmunds.svg";
import Facebook from "@/assets/icons/socialMedia/facebook.svg";
import Google from "@/assets/icons/socialMedia/google.svg";

// Types
import { GridColDef } from "@mui/x-data-grid-pro";
import { IAttributedUser, IReviewDetail, IReviewSiteSummary } from "@DataPages/types/socialMediaReviews/model";
import { IPlatformReviewCount, IUserReview, IUserReviewObj, IUsersReview } from "@/DataPages/components/CRM/SocialMediaReviews/interfaces";
import { StoreRowValue } from "@DataPages/types/socialMediaReviews/utils";

// Constants
const HEADER_CLASS = "table-header-align-right";

function getUniqueReputationColumnNames(data: IUserReviewObj[]): string[] {
	const allSiteNames = data.flatMap((review) => {
		return review.platformReviewCounts.map((platform: IPlatformReviewCount) => platform.siteName);
	});
	return [...new Set(allSiteNames)];
}

function getReputationHeaders(section: string = "sales"): GridColDef[] {
	if (section === "store") {
		return [
			{
				field: "_id",
				headerName: "ID",
				filterable: false,
			},
		];
	}

	return [
		{
			field: "_id",
			headerName: "ID",
			filterable: false,
		},
		{
			field: "employee_id",
			headerName: "employee_id",
			filterable: false,
		},
		{
			field: "name",
			headerName: "Associate",
			width: 180,
			headerClassName: "super-app-theme--header",
			editable: false,
			renderCell: (param) => {
				if (param.id === "auto-generated-group-footer-root") {
					return "Total";
				}
				return undefined;
			},
		},
	];
}

function extractColumnsFromReviewsNew(uniqueSitesColumns: string[], section: string = "sales"): GridColDef[] {
	const tableColumns = getReputationHeaders(section);

	uniqueSitesColumns.forEach((item) => {
		tableColumns.push({
			field: item.toLowerCase(),
			headerName: firstCapitalLetter(item),
			type: "number",
			align: "right",
			headerAlign: "right",
			flex: 1,
			sortable: section === "sales",
			headerClassName: HEADER_CLASS,
			renderHeader: () => {
				let icon = null;
				switch (item) {
					case "cars":
						icon = Cars;
						break;
					case "betterbusinessbureau":
						icon = BBB;
						break;
					case "carfax":
						icon = CarFax;
						break;
					case "cargurus":
						icon = CarGurus;
						break;
					case "dealerrater":
						icon = DealerRater;
						break;
					case "edmunds":
						icon = Edmunds;
						break;
					case "facebook":
						icon = Facebook;
						break;
					case "google":
						icon = Google;
						break;
					default:
						break;
				}

				return (
					<Stack direction={"row"} alignContent={"center"} spacing={1.25}>
						{icon && <img src={icon} style={{ width: "15px" }} alt={`${item}-icon`} />}
						<div className="MuiDataGrid-columnHeaderTitle">{firstCapitalLetter(item)}</div>
					</Stack>
				);
			},
		});
	});

	if (section === "sales") {
		tableColumns.push({
			field: "review",
			headerName: "Total",
			type: "number",
			width: 150,
			align: "right",
			headerClassName: "table-header-align-right",
		});
	}

	return tableColumns;
}

function getUserRows(users: IUserReviewObj[], sitesArray: string[]) {
	return users.map((user: IUserReviewObj) => {
		const platformCounts = user.platformReviewCounts.reduce((acc: Record<string, StoreRowValue>, curr) => {
			acc[curr.siteName] = { totalReviews: curr.reviewCount, neededResponseTotal: curr.neededResponseTotal };
			return acc;
		}, {});

		// Ensure all sites in sitesArray are present in the platformCounts
		sitesArray.forEach((site) => {
			if (!platformCounts[site]) {
				platformCounts[site] = { totalReviews: 0, neededResponseTotal: 0 };
			}
		});

		return {
			_id: user.user._id,
			reputationMgmtUserId: user.user.reputationMgmtUserId,
			employee_id: user.user.employeeId,
			name: user.user.fullName,
			review: { totalReviews: user.totalReviews },
			...platformCounts,
		};
	});
}

function prepareDataReputationReviewsNew(response: IUsersReview): Omit<IUserReview, "positionTitle"> {
	if (!response?.sitesReviews?.length) {
		return {
			positionName: "",
			rows: [],
			columns: [],
		};
	}

	const usersPositions: Record<string, string[]>[] = response.sitesReviews.map((position) => ({
		[position.name]: getUniqueReputationColumnNames(position.users),
	}));

	const updated = response.sitesReviews?.map((siteReview) => {
		const uniqueSitesArray = usersPositions
			.map((el) => el[siteReview.name])
			.filter(Boolean)
			.flat()
			.map((el) => el.toLowerCase());

		const rows = getUserRows(siteReview.users, uniqueSitesArray);
		const columns = extractColumnsFromReviewsNew(uniqueSitesArray);

		return {
			positionName: siteReview.name,
			rows: rows as unknown as IReviewSiteSummary[],
			columns,
		};
	});

	return updated[0];
}

function prepareDataStoreReviews(monthlyReviews: IReviewSiteSummary[]) {
	const data: { [key: string]: string | StoreRowValue }[] = [
		{
			_id: uuidv4(),
		},
	];

	monthlyReviews?.forEach((el) => (data[0][el.siteName] = { totalReviews: el.reviewCount, neededResponseTotal: el.neededResponseTotal }));

	return data;
}

function getStoreUniqueSiteNames(reviews: IReviewSiteSummary[]): string[] {
	const siteNames = reviews.filter((review) => review.reviewCount).map((review) => String(review.siteName));
	return Array.from(new Set(siteNames));
}

function filterByRating(data: IReviewDetail[] | null | undefined, selectedRatings: number[]): IReviewDetail[] {
	if (!Array.isArray(data)) return [];
	if (!selectedRatings?.length) return data;
	return data.filter((detail) => selectedRatings.includes(detail.review?.rating));
}

function filterBySearchCriteria(reviewDetails: IReviewDetail[], searchCriteria: string): IReviewDetail[] {
	const lowerCaseCriteria = searchCriteria.toLowerCase();
	return reviewDetails.filter((reviewDetail) => reviewDetail.review.body?.toLowerCase().includes(lowerCaseCriteria));
}

function filterByAssociates(reviewDetails: IReviewDetail[], selectedAssociates: IAttributedUser[]): IReviewDetail[] {
	if (!reviewDetails?.length) {
		return [];
	}

	return reviewDetails.filter((detail) =>
		detail.attributions.some((attribution) =>
			selectedAssociates.some((associate) => associate?.reputationMgmtUserId === attribution?.userUid)
		)
	);
}

export {
	extractColumnsFromReviewsNew,
	filterByAssociates,
	filterByRating,
	filterBySearchCriteria,
	firstCapitalLetter,
	getStoreUniqueSiteNames,
	getUniqueReputationColumnNames,
	prepareDataReputationReviewsNew,
	prepareDataStoreReviews,
};
